import type { CheckboxProps, RadioButtonProps } from '@shopify/polaris';
import type { GTooltipProps } from '~/presentation/ui';

import { BlockStack, Box, Checkbox, InlineStack, RadioButton, Text } from '@shopify/polaris';
import { ChoiceHelpText, GTooltip } from '~/presentation/ui';
import { useEffect, useState } from 'react';
import { cls } from '~/utils';

// Base interface for common properties
interface GCheckboxOptionBase {
  value: string;
  label: string;
  selected: boolean;
  tooltip?: GTooltipProps;
  description?: string;
}

export interface GCheckboxSingleOption
  extends GCheckboxOptionBase, Omit<CheckboxProps, 'value' | 'onChange' | 'label'> {}
export interface GCheckboxMultipleOption
  extends GCheckboxOptionBase, Omit<RadioButtonProps, 'value' | 'onChange' | 'label'> {}
interface GCheckboxSingleProps {
  multiple?: false;
  options: GCheckboxSingleOption[];
  onChange: (value: string) => void;
  id?: string;
}

interface GCheckboxMultipleProps {
  multiple: true;
  /**
   * @default false
   */
  emptySelected?: boolean;
  selected?: string[];
  options: GCheckboxMultipleOption[];
  onChange: (values: string[]) => void;
  onChangeSingleValue?: (value: string) => void;
  id?: string;
  itemHovered?: string;
  onHover?: (value?: string) => void;
  dependentDisabledValues?: Set<string>;
}

type GCheckboxProps = GCheckboxSingleProps | GCheckboxMultipleProps;

export const GCheckbox = ({ ...props }: GCheckboxProps) => {
  return props.multiple ? <GCheckboxMultiple {...props} /> : <GCheckboxSingle {...props} />;
};

export const GCheckboxMultiple = ({
  options,
  onChange,
  onChangeSingleValue,
  emptySelected,
  selected,
  onHover,
  itemHovered,
  dependentDisabledValues,
}: GCheckboxMultipleProps) => {
  const [settingOptions, setSettingOptions] = useState<string[]>([]);

  const handleChange = (value: string) => {
    if (onChangeSingleValue) {
      onChangeSingleValue(value);
    }
    const updatedOptions = settingOptions.includes(value)
      ? settingOptions.filter((item) => item !== value)
      : [...settingOptions, value];

    setSettingOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const handleHover = (value?: string) => {
    if (!onHover) return;

    onHover(value);
  };

  useEffect(() => {
    setSettingOptions(() => {
      if (selected) {
        return options.filter((item) => selected.includes(item.value)).map((item) => item.value);
      }

      return [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <BlockStack>
      {options.map((item) => {
        const hasForceUnchecked = dependentDisabledValues?.has(item.value);
        const isOnlyOneSelected =
          emptySelected && options.filter((opt) => opt.selected).length === 1 && emptySelected;

        const handleCheckboxClick = () => {
          if (isOnlyOneSelected) return;

          handleChange(item.value);
        };

        return (
          <div
            key={item.value}
            className={cls('flex items-center justify-between w-full', {
              'cursor-not-allowed': isOnlyOneSelected,
              'cursor-pointer': !isOnlyOneSelected,
              'bg-surface-hover rounded-lg': itemHovered === item.value,
            })}
            onClick={() => {
              if (isOnlyOneSelected || item.disabled) return;

              handleCheckboxClick();
            }}
            onMouseEnter={() => handleHover(item.value)}
            onMouseLeave={() => handleHover(undefined)}
          >
            {isOnlyOneSelected ? (
              <GTooltip isEnabled={item.tooltip?.isEnabled} {...item.tooltip}>
                <Box paddingBlock="150" paddingInline="200" width="100%">
                  <InlineStack blockAlign="center" gap="150">
                    <Checkbox label={item.label} labelHidden checked={item.selected} disabled />
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {item.label}
                    </Text>
                  </InlineStack>
                </Box>
              </GTooltip>
            ) : (
              <Box paddingBlock="150" paddingInline="200" minWidth="0">
                <InlineStack blockAlign="center" gap="150" wrap={false}>
                  <Checkbox
                    label={item.label}
                    labelHidden
                    checked={hasForceUnchecked ? false : settingOptions.includes(item.value)}
                    onChange={handleCheckboxClick}
                    disabled={item.disabled}
                  />
                  <Box minWidth="0">
                    <Text
                      as="p"
                      variant="bodyMd"
                      truncate
                      tone={hasForceUnchecked ? 'disabled' : 'inherit'}
                    >
                      {item.label}
                    </Text>
                    {item.description && (
                      <Text as="p" variant="bodySm" tone="disabled" truncate>
                        {item.description}
                      </Text>
                    )}
                  </Box>
                </InlineStack>
              </Box>
            )}
          </div>
        );
      })}
    </BlockStack>
  );
};

export const GCheckboxSingle = ({ options, onChange }: GCheckboxSingleProps) => {
  return (
    <BlockStack gap="300">
      {options.map((item) => (
        <RadioButton
          key={item.value}
          label={item.label}
          checked={item.selected}
          onChange={() => onChange(item.value)}
          helpText={item.helpText ? <ChoiceHelpText item={item} /> : undefined}
        />
      ))}
    </BlockStack>
  );
};
